import { motion } from "framer-motion";
import { Target, RefreshCw, Cpu, TrendingUp, Calculator } from "lucide-react";
import { useTranslation } from "../i18n/LanguageContext";

const SPECIALIST_ICONS = [RefreshCw, Cpu, TrendingUp, Calculator];

const container = { hidden: { opacity: 0 }, show: { opacity: 1, transition: { staggerChildren: 0.1 } } };
const item = { hidden: { opacity: 0, y: 24 }, show: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } } };

export default function NetworkSection() {
  const { t } = useTranslation();
  const n = t.network;

  return (
    <section id="network" className="py-24 md:py-32 bg-[#0A0A0B]" data-testid="network-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7, ease: "easeOut" }}
          className="mb-16"
        >
          <div className="flex items-center gap-4 mb-6">
            <motion.span
              initial={{ scaleX: 0 }}
              whileInView={{ scaleX: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, ease: "easeOut" }}
              className="inline-block w-10 h-px bg-[#C9A96E]"
              style={{ transformOrigin: "left center" }}
            />
            <span className="fwd-eyebrow">{n.eyebrow}</span>
          </div>
          <h2 className="fwd-heading">
            {n.headline1}
            <br />
            <span className="fwd-italic-gold">{n.headlineItalic}</span>
          </h2>
          <p className="fwd-body mt-6" style={{ maxWidth: "640px" }}>
            {n.intro}
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7, ease: "easeOut" }}
          className="mb-8"
        >
          <div
            className="p-8 md:p-12 fwd-card-hover"
            style={{
              background: "#141417",
              border: "1px solid #C9A96E",
              borderRadius: "2px",
            }}
          >
            <div className="flex items-start gap-6">
              <div
                className="w-14 h-14 flex items-center justify-center shrink-0"
                style={{
                  background: "rgba(201, 169, 110, 0.1)",
                  border: "1px solid rgba(201, 169, 110, 0.3)",
                  borderRadius: "2px",
                }}
              >
                <Target size={26} style={{ color: "#C9A96E" }} strokeWidth={1.5} />
              </div>
              <div>
                <span className="fwd-eyebrow block mb-3" style={{ fontSize: "16px" }}>
                  {n.coreOfferingLabel}
                </span>
                <h3 className="fwd-heading mb-4">{n.coreOfferingTitle}</h3>
                <p className="fwd-body">{n.coreOfferingDesc}</p>
              </div>
            </div>
          </div>
        </motion.div>

        <motion.div
          variants={container}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: "-100px" }}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5"
        >
          {n.specialists.map((s, index) => {
            const Icon = SPECIALIST_ICONS[index] || RefreshCw;
            return (
              <motion.div
                key={index}
                variants={item}
                className="p-8 fwd-card-hover"
                style={{
                  background: "#141417",
                  border: "1px solid #2A2A2E",
                  borderRadius: "2px",
                }}
                data-testid={`specialist-${index}`}
              >
                <Icon size={22} style={{ color: "#C9A96E" }} strokeWidth={1.5} className="mb-5" />
                <h4 className="fwd-heading mb-3" style={{ fontSize: "22px" }}>{s.label}</h4>
                <p className="fwd-body" style={{ fontSize: "17px" }}>{s.description}</p>
              </motion.div>
            );
          })}
        </motion.div>
      </div>
    </section>
  );
}
